'use client'

import { useState, useCallback, useEffect } from 'react'
import { useAuth } from '@/hooks/use-auth'
import type { Voicemail } from '@/lib/pbx/flow/types'

export function useVoicemails(limit = 50, offset = 0) {
  const [voicemails, setVoicemails] = useState<Voicemail[]>([])
  const [total, setTotal] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const { fetchWithAuth, isAuthenticated } = useAuth()

  const loadVoicemails = useCallback(async () => {
    if (!isAuthenticated) return

    setIsLoading(true)
    setError(null)

    try {
      const res = await fetchWithAuth(`/api/pbx/voicemails?limit=${limit}&offset=${offset}`)
      if (!res.ok) {
        const data = await res.json()
        throw new Error(data.error || 'Failed to fetch voicemails')
      }
      const data = await res.json()
      setVoicemails(data.voicemails || [])
      setTotal(data.total || 0)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error'
      setError(message)
    } finally {
      setIsLoading(false)
    }
  }, [fetchWithAuth, isAuthenticated, limit, offset])

  useEffect(() => {
    loadVoicemails()
  }, [loadVoicemails])

  return {
    voicemails,
    total,
    isLoading,
    error,
    refetch: loadVoicemails,
  }
}

export function useVoicemailMutations() {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { fetchWithAuth } = useAuth()

  const markAsRead = useCallback(async (id: string) => {
    setIsLoading(true)
    setError(null)

    try {
      const res = await fetchWithAuth(`/api/pbx/voicemails/${id}/read`, {
        method: 'POST',
      })

      if (!res.ok) {
        const data = await res.json()
        throw new Error(data.error || 'Failed to mark as read')
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error'
      setError(message)
      throw err
    } finally {
      setIsLoading(false)
    }
  }, [fetchWithAuth])

  const deleteVoicemail = useCallback(async (id: string) => {
    setIsLoading(true)
    setError(null)

    try {
      const res = await fetchWithAuth(`/api/pbx/voicemails/${id}`, {
        method: 'DELETE',
      })

      if (!res.ok) {
        const data = await res.json()
        throw new Error(data.error || 'Failed to delete voicemail')
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error'
      setError(message)
      throw err
    } finally {
      setIsLoading(false)
    }
  }, [fetchWithAuth])

  return {
    markAsRead,
    deleteVoicemail,
    isLoading,
    error,
  }
}
